import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ParamContext } from '../../service/param.context';
import { HttpClient, HttpContext, HttpParams, HttpResponse } from '@angular/common/http';
import { SERVER_API_URL } from '../../../app.constants';
import { Lot, LotAttribute, LotAttributeColumnModel, LotAttributeRequestModel } from '../model/lot.model';
import { createRequestOption } from '../..';
import { map } from 'rxjs/operators';
import { LotSearch } from '../model/lot-search.model';
import { SearchComposite } from '../../model/search-composite';
import { Variable } from '../../ontology/model/variable';
import { VariableTypeEnum } from '../../ontology/variable-type.enum';
import { LotTemplateExportType } from './lot-template-export-type';
import { EnableLoadingIndicator, EnableSavingIndicator } from '../../loading-indicator';

export type EntityResponseType = HttpResponse<Lot>;

@Injectable({ providedIn: 'root' })
export class LotService {

    constructor(private context: ParamContext,
                private http: HttpClient) {
    }

    createLots(lotGeneratorBatchRequest): Observable<string[]> {
        return this.http.post<any>(SERVER_API_URL + `crops/${this.context.cropName}/lots/generation?programUUID=` + this.context.programUUID,
            lotGeneratorBatchRequest, { context: new HttpContext().set(EnableSavingIndicator, true) });
    }

    getLotsByGId(gid: number, request: any): Observable<HttpResponse<Lot[]>> {
        if (this.context.programUUID) {
            request['programUUID'] = this.context.programUUID;
        }

        const url = SERVER_API_URL + `crops/${this.context.cropName}/lots/germplasm/${gid}`;
        const params = createRequestOption(request);
        return this.http.get<Lot[]>(url, { params, observe: 'response' });

    }

    createLotAttribute(lotId: number, lotAttributeRequestModel: LotAttributeRequestModel): Observable<number> {
        let url = SERVER_API_URL + `crops/${this.context.cropName}/lot/${lotId}/attributes`;
        if (this.context.programUUID) {
            url += '?programUUID=' + this.context.programUUID;
        }
        return this.http.post<number>(url, lotAttributeRequestModel);
    }

    updateLotAttribute(gid: number, attributeId: number, lotAttributeRequestModel: LotAttributeRequestModel): Observable<any> {
        const url = SERVER_API_URL + `crops/${this.context.cropName}/lot/${gid}/attributes/${attributeId}` +
            '?programUUID=' + this.context.programUUID;
        return this.http.patch<any>(url, lotAttributeRequestModel);
    }

    deleteLotAttribute(lotId: number, attributeId: number) {
        const url = SERVER_API_URL + `crops/${this.context.cropName}/lot/${lotId}/attributes/${attributeId}` +
            '?programUUID=' + this.context.programUUID;
        return this.http.delete<any>(url);
    }

    create(lot: Lot, stockIdPrefix: string) {
        let url =  SERVER_API_URL + `crops/${this.context.cropName}/lots`;
        if (this.context.programUUID) {
            url += '?programUUID=' + this.context.programUUID;
        }
        return this.http.post(url,
            Object.assign({ generateStock: true, stockPrefix: stockIdPrefix }, lot), {
                observe: 'response',
                responseType: 'text'
            })
            .pipe(map((res: any) => res.body));
    }

    /**
     * @param lotsUpdateRequest information to be updated
     */
    update(lotsUpdateRequest) {
        let url =  SERVER_API_URL + `crops/${this.context.cropName}/lot-lists`;
        if (this.context.programUUID) {
            url += '?programUUID=' + this.context.programUUID;
        }
        return this.http.patch<Lot>(url, Object.assign(lotsUpdateRequest)
            , { observe: 'response' }).pipe(map((res: any) => res.body));
    }

    updateBalance(lotUpdateBalanceRequest): Observable<EntityResponseType> {
        let url =  SERVER_API_URL + `crops/${this.context.cropName}/lot-lists/balance`;
        if (this.context.programUUID) {
            url += '?programUUID=' + this.context.programUUID;
        }
        return this.http.patch<Lot>(url, Object.assign(lotUpdateBalanceRequest)
            , { observe: 'response' }).pipe(map((res: EntityResponseType) => res));
    }

    find(id: number): Observable<EntityResponseType> {
        let url =  SERVER_API_URL + `crops/${this.context.cropName}/lots/search/${id}`;
        if (this.context.programUUID) {
            url += '?programUUID=' + this.context.programUUID;
        }
        return this.http.get<Lot>(url, { observe: 'response' })
            .pipe(map((res: EntityResponseType) => res));
    }

    getSearchResults(req?: any): Observable<HttpResponse<Lot[]>> {
        const options = createRequestOption(req);
        let url =  SERVER_API_URL + `crops/${this.context.cropName}/lots/search`;
        if (this.context.programUUID) {
            url += '?programUUID=' + this.context.programUUID;
        }
        return this.http.get<Lot[]>(url, { params: options, observe: 'response', context: new HttpContext().set(EnableLoadingIndicator, true) });
    }

    delete(id: number): Observable<HttpResponse<any>> {
        const url =  SERVER_API_URL + `crops/${this.context.cropName}/lots/${id}`;
        return this.http.delete<any>(url, { observe: 'response' });
    }

    search(req?: LotSearch): Observable<string> {
        let url =  SERVER_API_URL + `crops/${this.context.cropName}/lots/search`;
        if (this.context.programUUID) {
            url += '?programUUID=' + this.context.programUUID;
        }
        return this.http.post<any>(url, req, { observe: 'response', context: new HttpContext().set(EnableLoadingIndicator, true) })
            .pipe(map((res: any) => res.body.searchResultDbId));
    }

    getById(lotUUID: string): Promise<Lot> {
        let url =  SERVER_API_URL + `crops/${this.context.cropName}/lots/search`;
        if (this.context.programUUID) {
            url += '?programUUID=' + this.context.programUUID;
        }
        return this.http.post<any>(url, { lotUUIDs: [lotUUID] }, { observe: 'response' })
            .toPromise().then((res) => res.body.searchResultDbId)
            .then((searchRequestId: string) => {
                return this.http.get<Lot[]>(url, { params: { searchRequestId }, observe: 'response' })
                    .toPromise();
            })
            .then((res) => res.body && res.body[0]);
    }

    importLotsWithInitialBalance(lotImportRequest: any): Observable<EntityResponseType> {
        let url =  SERVER_API_URL + `crops/${this.context.cropName}/lot-lists`;
        if (this.context.programUUID) {
            url += '?programUUID=' + this.context.programUUID;
        }
        return this.http.post<Lot>(url, lotImportRequest, { observe: 'response' })
            .pipe(map((res: EntityResponseType) => res));
    }

    downloadLotsTemplate(templateExportType: LotTemplateExportType): Observable<HttpResponse<Blob>> {
        const options: HttpParams = new HttpParams();
        let url =  SERVER_API_URL + `crops/${this.context.cropName}/lot-lists/templates/xlsx?lotExcelTemplateExportType=${templateExportType}`;
        if (this.context.programUUID) {
            url += '&programUUID=' + this.context.programUUID;
        }
        return this.http
            .get(url, {
                params: options,
                responseType: 'blob',
                observe: 'response'
            });
    }

    getSearchMetaData(searchComposite: SearchComposite<any, any>): Observable<HttpResponse<any>> {
        let url =  SERVER_API_URL + `crops/${this.context.cropName}/lots/metadata`;
        if (this.context.programUUID) {
            url += '?programUUID=' + this.context.programUUID;
        }
        return this.http.post<any>(url, searchComposite, { observe: 'response', context: new HttpContext().set(EnableLoadingIndicator, true) });
    }

    closeLot(searchComposite: SearchComposite<any, any>) {
        const url =  SERVER_API_URL + `crops/${this.context.cropName}/lots/close`;
        return this.http.post<any>(
            url, searchComposite, { observe: 'response' })
            .pipe(map((res) => res.body));
    }

    mergeLot(lotMergeRequestDto: any) {
        const url =  SERVER_API_URL + `crops/${this.context.cropName}/lots/merge`;
        return this.http.post<any>(
            url, lotMergeRequestDto, { observe: 'response' })
            .pipe(map((res) => res.body));
    }

    splitLot(lotSplitRequestDto: any) {
        const url =  SERVER_API_URL + `crops/${this.context.cropName}/lots/split`;
        return this.http.post<any>(
            url, lotSplitRequestDto, { observe: 'response' })
            .pipe(map((res) => res.body));
    }

    getLotAttributes(lotId: string): Observable<LotAttribute[]> {
        let url =  SERVER_API_URL + `crops/${this.context.cropName}/lot/${lotId}/attributes`;
        if (this.context.programUUID) {
            url += '?programUUID=' + this.context.programUUID;
        }

        return this.http.get<LotAttribute[]>(url);
    }

    searchAttributes(query): Observable<HttpResponse<Variable[]>> {
        let url =  SERVER_API_URL + `crops/${this.context.cropName}/variables/attributes/search?query=${query}&variableTypeIds=${VariableTypeEnum.INVENTORY_ATTRIBUTE}`;
        if (this.context.programUUID) {
            url += '&programUUID=' + this.context.programUUID;
        }

        return this.http.get<Variable[]>(url, { observe: 'response' });
    }

    getLotAttributeColumns(): Observable<HttpResponse<LotAttributeColumnModel[]>> {
        const url =  SERVER_API_URL + `crops/${this.context.cropName}/programs/${this.context.programUUID}/lots/attributes/columns`;
        return this.http.get<LotAttributeColumnModel[]>(url,
            { observe: 'response' });
    }

    validateLabelsInfoInputData(request: any): Observable<void> {
        const url =  SERVER_API_URL + `crops/${this.context.cropName}/programs/${this.context.programUUID}/labelPrinting/Lot/labels/validate`;
        return this.http.post<void>(url, request);
    }
}
